/**
 * Typed specs for inline chat charts.
 *
 * One discriminated union covers every chart the MARS portal can show.
 * The agent calls `propose_chart` with a `name` + the args for that
 * name; the SPA renders via the existing portal chart builders so the
 * chat visual always matches the corresponding portal tab.
 *
 * v1 wires up SVAR (cumulative + week-over-week). Other names are
 * valid in the schema but render a "coming soon" placeholder until
 * their builder is wired in.
 */

export type SvarVariableKey =
  | 'sp500' | 'oil' | 'copper' | 'usdtwi' | '10yty'
  | '10yTipsYield' | '10yBreakeven' | '3mtbill' | 'baasp' | 'gsFci';

export type MacroFanVariable =
  | 'GDP' | 'Inflation' | 'T-Bill' | '10 Year' | 'BAA Spread' | 'Dividend Yield';

export type Mandate = 'fqm' | 'dar_nn' | 'fidelity_balanced' | 'fidelity_conservative';
export type Asset   = 'equities' | 'bonds' | 'commodities' | 'inflation';

interface SvarSpecBase {
  variable: SvarVariableKey;
  start_date?: string | null;
  end_date?: string | null;
}

export type ChartSpec =
  | ({ name: 'svar_cumulative' }       & SvarSpecBase)
  | ({ name: 'svar_wow' }              & SvarSpecBase)
  | { name: 'macro_fan';               variable: MacroFanVariable; vintage?: string | null }
  | { name: 'term_structure_curve';    mandate: Mandate; asset: Asset; old_date: string; new_date: string }
  | { name: 'term_structure_decomp';   mandate: Mandate; asset: Asset; old_date: string; new_date: string }
  | { name: 'expected_returns_curve';  asset: Asset; date: string; return_type?: 'excess' | 'nominal' | 'real' }
  | { name: 'asset_class_volatility';  assets?: Asset[]; start_date?: string | null; end_date?: string | null }
  | { name: 'portfolio_exposures';     fund: Mandate; start_date?: string | null; end_date?: string | null };

export function isChartSpec(x: unknown): x is ChartSpec {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  if (typeof o.name !== 'string') return false;
  const names: ReadonlySet<string> = new Set([
    'svar_cumulative', 'svar_wow',
    'macro_fan', 'term_structure_curve', 'term_structure_decomp',
    'expected_returns_curve', 'asset_class_volatility', 'portfolio_exposures',
  ]);
  return names.has(o.name);
}
